import { versionNames } from '../../lib/format';
import type { TicketListItem } from '../../api/types';
import type { Version } from './versions-api';

/**
 * 🔴 `version` AND `fix_version` ARE DIFFERENT RELATIONSHIPS, AND USERS CONFLATE THEM.
 *
 *   `ticket.version_id`        — ONE version. The release this work was PLANNED for.
 *   `ticket_fix_version`       — MANY versions. The releases the work actually SHIPPED IN.
 *
 * ⭐⭐ They are counted, labelled and listed separately here and everywhere above here. Merging them
 * into one "tickets in this version" number is the obvious simplification and it is wrong twice: it
 * over-counts a ticket that is in both, and it reports work that slipped as though it had shipped.
 *
 * ⭐ `fix_version` IS MULTI-VALUED. Or's written field list said it was singular; his own screenshot
 * shows `v1.0.33, v1.0.34` on one row, and the spec was corrected from the screenshot. A ticket that
 * shipped in two versions is therefore listed under BOTH — never under the first one found.
 *
 * ⚠️ MATCHED BY NAME, because that is what the API sends: `TICKET_SELECT` aggregates
 * `array_agg(fver.name)`, a `text[]` of names with no ids. `UNIQUE (project_id, name)` in db/001
 * makes a name unambiguous WITHIN one project — and both callers load one project at a time, which
 * is the condition this rests on.
 */

export interface VersionRollup {
  version: Version;
  /** `ticket.version_id === version.id` — planned for this release. The ones we LOADED. */
  plannedIn: TicketListItem[];
  /** `version.name ∈ ticket.fix_versions` — shipped in this release. The ones we LOADED. */
  fixedIn: TicketListItem[];
  /** In BOTH lists. Surfaced so the two counts are never read as double-counting. */
  inBothCount: number;
  /**
   * ⭐⭐ THE PROJECT-WIDE TOTALS FROM THE API, when it sent them. These are the numbers on screen —
   * the lists above are only what this client's bounded ticket window could show, so a total of 8
   * beside a list of 3 is a fact worth stating and NOT a discrepancy to be hidden by printing 3.
   * `null` means the API did not send one; ⛔ it is never defaulted to the list length, which would
   * turn "we could not ask" into a confident wrong number.
   */
  plannedTotal: number | null;
  fixedTotal: number | null;
}

function count(value: number | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function buildVersionRollups(
  versions: readonly Version[],
  tickets: readonly TicketListItem[],
): VersionRollup[] {
  return versions.map((version) => {
    const plannedIn = tickets.filter((ticket) => ticket.version_id === version.id);
    const fixedIn = tickets.filter((ticket) => fixVersionsOf(ticket).includes(version.name));
    const planned = new Set(plannedIn.map((ticket) => ticket.id));

    return {
      version,
      plannedIn,
      fixedIn,
      inBothCount: fixedIn.filter((ticket) => planned.has(ticket.id)).length,
      plannedTotal: count(version.version_ticket_count),
      fixedTotal: count(version.fix_version_ticket_count),
    };
  });
}

/**
 * How a count is written: the API's exact project-wide total when there is one, the loaded count as a
 * FLOOR when there is not.
 * ⭐ Two different facts, two different renderings, and the caller cannot accidentally print a floor
 * where an exact number exists.
 */
export function describeCount(
  total: number | null,
  loaded: number,
  windowIsComplete: boolean,
): { text: string; isExact: boolean; hiddenFromList: number } {
  if (total !== null) {
    return { text: String(total), isExact: true, hiddenFromList: Math.max(0, total - loaded) };
  }
  return {
    text: windowIsComplete ? String(loaded) : `${loaded}+`,
    isExact: windowIsComplete,
    hiddenFromList: 0,
  };
}

/**
 * ⚠️ `fix_versions` has had TWO shapes in this codebase's lifetime — `{id,name}[]` was the guess,
 * `text[]` of names is the truth (`680ab8b`). Both go through `versionNames()`, in one place, because
 * `.map(v => v.name)` over an array of strings prints `undefined` on every row and reads as bad data
 * rather than as a client reading the wrong shape.
 *
 * ⛔ `undefined` is NOT `[]`. A missing key means the endpoint did not return the field; an empty
 * array means the ticket genuinely ships in nothing. `hasFixVersionData` tells them apart.
 */
export function fixVersionsOf(ticket: TicketListItem): string[] {
  return versionNames(ticket.fix_versions ?? []);
}

export function hasFixVersionData(tickets: readonly TicketListItem[]): boolean {
  return tickets.every((ticket) => ticket.fix_versions !== undefined);
}

/**
 * Fix-version names present on tickets that match no version we were given.
 * ⭐ Should be empty — the names come from the `version` table by join. If it is not, the versions
 * list and the tickets disagree, and that is worth saying rather than silently not drawing them.
 */
export function orphanFixVersionNames(
  versions: readonly Version[],
  tickets: readonly TicketListItem[],
): string[] {
  const known = new Set(versions.map((version) => version.name));
  const orphans = new Set<string>();
  for (const ticket of tickets) {
    for (const name of fixVersionsOf(ticket)) {
      if (!known.has(name)) orphans.add(name);
    }
  }
  return [...orphans].sort();
}

/**
 * The version names observed on tickets, when the versions endpoint itself is unavailable.
 * ⛔ THIS IS NOT A VERSION LIST AND MUST NEVER BE PRESENTED AS ONE: it cannot contain a version with
 * no tickets, and it has no state, no release date and no description. It exists so the view degrades
 * to something true instead of to a blank page.
 */
export function derivedVersionNames(tickets: readonly TicketListItem[]): string[] {
  const names = new Set<string>();
  for (const ticket of tickets) {
    for (const name of fixVersionsOf(ticket)) names.add(name);
  }
  return [...names].sort(compareVersionNames);
}

/** `v1.0.9` before `v1.0.33` — a plain string sort puts 33 first and looks like a data error. */
export function compareVersionNames(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

export interface ReleaseHistory {
  /** Released, newest first. */
  dated: VersionRollup[];
  /**
   * ⚠️ Released but with NO release_date. Kept in their own group rather than sorted as if the date
   * were the epoch, which would silently park them at the bottom of a list ordered by date and imply
   * they were the oldest releases.
   */
  undated: VersionRollup[];
}

export function buildReleaseHistory(rollups: readonly VersionRollup[]): ReleaseHistory {
  const released = rollups.filter((rollup) => rollup.version.state === 'released');

  const dated = released
    .filter((rollup) => Boolean(rollup.version.release_date))
    .sort((a, b) => (b.version.release_date ?? '').localeCompare(a.version.release_date ?? ''));

  const undated = released.filter((rollup) => !rollup.version.release_date);

  return { dated, undated };
}

/** Versions in the list, newest-looking first: unreleased on top, then released by date. */
export function sortVersionsForDisplay(rollups: readonly VersionRollup[]): VersionRollup[] {
  const rank: Record<string, number> = { unreleased: 0, released: 1, archived: 2 };
  return [...rollups].sort((a, b) => {
    const byState = (rank[a.version.state] ?? 3) - (rank[b.version.state] ?? 3);
    if (byState !== 0) return byState;
    const byDate = (b.version.release_date ?? '').localeCompare(a.version.release_date ?? '');
    if (byDate !== 0) return byDate;
    return compareVersionNames(b.version.name, a.version.name);
  });
}
